import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getWorkouts, deleteWorkout, cloneWorkout, publishWorkout } from '../api/workouts';
import { getTags } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';
import TagBadge from '../components/common/TagBadge';
import DifficultyStars from '../components/common/DifficultyStars';
import ConfirmModal from '../components/common/ConfirmModal';

const FORMAT_LABELS = { tabata: 'Tabata', hiit: 'HIIT', circuit: 'Circuit', custom: 'Custom' };
const FORMAT_COLORS = { tabata: 'danger', hiit: 'warning', circuit: 'info', custom: 'secondary' };

export default function WorkoutListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit = user?.role === 'admin' || user?.role === 'trainer' || isSuperAdmin;

  const [workouts, setWorkouts] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filterTag, setFilterTag] = useState('');
  const [filterFormat, setFilterFormat] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterPublished, setFilterPublished] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // workout to delete

  useEffect(() => {
    Promise.all([getTags(), loadWorkouts()])
      .then(([t]) => setTags(t))
      .catch((e) => setError(e.message));
  }, [user?.id]); // eslint-disable-line

  async function loadWorkouts(filters = {}) {
    setLoading(true);
    try {
      const data = await getWorkouts(filters);
      setWorkouts(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function applyFilters(e) {
    e?.preventDefault();
    loadWorkouts({
      tag_id: filterTag || undefined,
      format_type: filterFormat || undefined,
      difficulty: filterDifficulty || undefined,
      published_only: filterPublished || undefined,
    });
  }

  function resetFilters() {
    setFilterTag('');
    setFilterFormat('');
    setFilterDifficulty('');
    setFilterPublished(false);
    loadWorkouts();
  }

  async function handleDelete() {
    try {
      await deleteWorkout(confirmDelete.id);
      setWorkouts((prev) => prev.filter((w) => w.id !== confirmDelete.id));
      setSuccess('Workout gelöscht.');
    } catch (e) {
      setError(e.message);
    } finally {
      setConfirmDelete(null);
    }
  }

  async function handleClone(workout) {
    try {
      const cloned = await cloneWorkout(workout.id);
      setWorkouts((prev) => [cloned, ...prev]);
      setSuccess(`Workout "${cloned.name}" wurde geklont.`);
    } catch (e) {
      setError(e.message);
    }
  }

  async function handlePublishToggle(workout) {
    try {
      const updated = await publishWorkout(workout.id, !workout.published);
      setWorkouts((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 fw-bold">
          <i className="bi bi-journal-richtext me-2 text-primary" />
          Workouts
        </h4>
        {canEdit && (
          <Link className="btn btn-primary" to="/workouts/new">
            <i className="bi bi-plus-lg me-1" />
            Neues Workout
          </Link>
        )}
      </div>

      <AlertBanner message={error} type="danger" onDismiss={() => setError('')} />
      <AlertBanner message={success} type="success" onDismiss={() => setSuccess('')} />

      {/* Filter bar */}
      <form className="card card-body mb-4 p-3" onSubmit={applyFilters}>
        <div className="row g-2 align-items-end">
          <div className="col-sm-3">
            <label className="form-label small mb-1">Tag</label>
            <select className="form-select form-select-sm" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
              <option value="">Alle Tags</option>
              {tags.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label small mb-1">Format</label>
            <select className="form-select form-select-sm" value={filterFormat} onChange={(e) => setFilterFormat(e.target.value)}>
              <option value="">Alle</option>
              {Object.entries(FORMAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label small mb-1">Schwierigkeit</label>
            <select className="form-select form-select-sm" value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}>
              <option value="">Alle</option>
              {[1, 2, 3, 4, 5].map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="col-sm-2 d-flex align-items-end pb-1">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="pubOnly"
                checked={filterPublished}
                onChange={(e) => setFilterPublished(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="pubOnly">Nur veröffentlicht</label>
            </div>
          </div>
          <div className="col-sm-3 d-flex gap-2">
            <button type="submit" className="btn btn-sm btn-outline-primary">
              <i className="bi bi-funnel me-1" />Filtern
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>
              Zurücksetzen
            </button>
          </div>
        </div>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : workouts.length === 0 ? (
        <div className="text-center text-muted py-5">
          <i className="bi bi-journal-x fs-1 d-block mb-2" />
          Keine Workouts gefunden.
          {canEdit && (
            <div className="mt-3">
              <Link className="btn btn-primary" to="/workouts/new">Erstes Workout erstellen</Link>
            </div>
          )}
        </div>
      ) : (
        <div className="row g-3">
          {workouts.map((w) => (
            <div key={w.id} className="col-sm-6 col-xl-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0 fw-semibold">{w.name}</h6>
                    <span className={`badge bg-${FORMAT_COLORS[w.format] || 'secondary'} ms-2`}>
                      {FORMAT_LABELS[w.format] || w.format}
                    </span>
                    {isSuperAdmin && w.is_global && <span className="badge bg-warning text-dark ms-1">Global</span>}
                    {isSuperAdmin && w.is_free && <span className="badge bg-success ms-1">Umsonst</span>}
                  </div>
                  {w.description && (
                    <p className="card-text text-muted small mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {w.description}
                    </p>
                  )}
                  <div className="d-flex align-items-center gap-3 mb-2 small text-muted">
                    <DifficultyStars value={w.difficulty} />
                    {w.duration && <span><i className="bi bi-clock me-1" />{w.duration} Min.</span>}
                  </div>
                  <div className="mb-2">
                    {w.tags?.map((t) => <TagBadge key={t.id} tag={t} />)}
                  </div>
                  <div>
                    {w.published ? (
                      <span className="badge bg-success"><i className="bi bi-check-circle me-1" />Veröffentlicht</span>
                    ) : (
                      <span className="badge bg-secondary"><i className="bi bi-pencil me-1" />Entwurf</span>
                    )}
                  </div>
                </div>
                {canEdit && (isSuperAdmin || !w.is_global) && (
                  <div className="card-footer bg-transparent d-flex gap-1 flex-wrap">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      title="Bearbeiten"
                      onClick={() => navigate(`/workouts/${w.id}/edit`)}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      title="Klonen"
                      onClick={() => handleClone(w)}
                    >
                      <i className="bi bi-copy" />
                    </button>
                    <button
                      className={`btn btn-sm ${w.published ? 'btn-outline-warning' : 'btn-outline-success'}`}
                      title={w.published ? 'Veröffentlichung zurückziehen' : 'Veröffentlichen'}
                      onClick={() => handlePublishToggle(w)}
                    >
                      <i className={`bi ${w.published ? 'bi-eye-slash' : 'bi-eye'}`} />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger ms-auto"
                      title="Löschen"
                      onClick={() => setConfirmDelete(w)}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        show={!!confirmDelete}
        title="Workout löschen"
        message={`Workout "${confirmDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
